import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { fetchSettings, patchSettings } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { toast } from "../../hooks/use-toast";

const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";
const ICONS = ["Zap", "Lock", "BadgeCheck", "MessageCircle", "Package", "Rocket", "Truck", "Headphones", "Clapperboard", "Gem"];

const Section = ({ title, children, onAdd }) => (
  <div className="bg-navy-800 rounded-2xl border border-white/10 p-6">
    <div className="flex items-center justify-between mb-5"><h3 className="text-white font-bold text-lg">{title}</h3>{onAdd && <button onClick={onAdd} className="inline-flex items-center gap-2 text-sm text-gold hover:underline"><Plus size={14} /> Add</button>}</div>
    {children}
  </div>
);

const ContentPanel = () => {
  const { reload } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400">Loading...</div>;

  const arr = (key) => s[key] || [];
  const setArr = (key, v) => setS((p) => ({ ...p, [key]: v }));
  const updItem = (key, i, patch) => setArr(key, arr(key).map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeItem = (key, i) => setArr(key, arr(key).filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const howTo = arr("howToOrder").map((x, i) => ({ ...x, step: i + 1 }));
      await patchSettings({ trustBadges: arr("trustBadges"), whyBuy: arr("whyBuy"), howToOrder: howTo, faqs: arr("faqs") });
      await reload();
      toast({ title: "Saved", description: "Content updated." });
    } catch { toast({ title: "Error", description: "Could not save." }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Section title="Trust Badges" onAdd={() => setArr("trustBadges", [...arr("trustBadges"), { icon: "Zap", label: "" }])}>
        <div className="space-y-3">
          {arr("trustBadges").map((b, i) => (
            <div key={i} className="flex gap-3 items-end">
              <div className="w-44"><label className={label}>Icon</label><select className={input} value={b.icon} onChange={(e) => updItem("trustBadges", i, { icon: e.target.value })}>{ICONS.map((ic) => <option key={ic}>{ic}</option>)}</select></div>
              <div className="flex-1"><label className={label}>Label</label><input className={input} value={b.label} onChange={(e) => updItem("trustBadges", i, { label: e.target.value })} /></div>
              <button onClick={() => removeItem("trustBadges", i)} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Why Buy From Us" onAdd={() => setArr("whyBuy", [...arr("whyBuy"), { icon: "Gem", title: "", text: "" }])}>
        <div className="space-y-3">
          {arr("whyBuy").map((w, i) => (
            <div key={i} className="bg-navy-700 rounded-xl p-3 grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-2"><label className={label}>Icon</label><select className={input} value={w.icon} onChange={(e) => updItem("whyBuy", i, { icon: e.target.value })}>{ICONS.map((ic) => <option key={ic}>{ic}</option>)}</select></div>
              <div className="sm:col-span-3"><label className={label}>Title</label><input className={input} value={w.title} onChange={(e) => updItem("whyBuy", i, { title: e.target.value })} /></div>
              <div className="sm:col-span-6"><label className={label}>Text</label><input className={input} value={w.text} onChange={(e) => updItem("whyBuy", i, { text: e.target.value })} /></div>
              <div className="sm:col-span-1 flex justify-end"><button onClick={() => removeItem("whyBuy", i)} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="How To Order Steps" onAdd={() => setArr("howToOrder", [...arr("howToOrder"), { step: arr("howToOrder").length + 1, title: "", text: "" }])}>
        <div className="space-y-3">
          {arr("howToOrder").map((st, i) => (
            <div key={i} className="bg-navy-700 rounded-xl p-3 grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-1"><label className={label}>Step</label><div className={`${input} text-center`}>{i + 1}</div></div>
              <div className="sm:col-span-4"><label className={label}>Title</label><input className={input} value={st.title} onChange={(e) => updItem("howToOrder", i, { title: e.target.value })} /></div>
              <div className="sm:col-span-6"><label className={label}>Text</label><input className={input} value={st.text} onChange={(e) => updItem("howToOrder", i, { text: e.target.value })} /></div>
              <div className="sm:col-span-1 flex justify-end"><button onClick={() => removeItem("howToOrder", i)} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="FAQ" onAdd={() => setArr("faqs", [...arr("faqs"), { q: "", a: "" }])}>
        <div className="space-y-3">
          {arr("faqs").map((f, i) => (
            <div key={i} className="bg-navy-700 rounded-xl p-3">
              <div className="flex gap-3 items-start">
                <div className="flex-1 space-y-2">
                  <input className={input} value={f.q} onChange={(e) => updItem("faqs", i, { q: e.target.value })} placeholder="Question" />
                  <textarea className={`${input} h-20 resize-none`} value={f.a} onChange={(e) => updItem("faqs", i, { a: e.target.value })} placeholder="Answer" />
                </div>
                <button onClick={() => removeItem("faqs", i)} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
    </div>
  );
};

export default ContentPanel;
